import type { initWasm, Resvg, ResvgRenderOptions } from '@resvg/resvg-wasm';

import { pathJoiner } from './resourceUtils';

// Re-export types for use by consumers
export type { ResvgRenderOptions };

// Define the constructor type explicitly and export it
export type ResvgConstructor = typeof Resvg;

// Global WASM modules - will be initialized once
let resvgWasm:
  | {
      initWasm: typeof initWasm;
      Resvg: typeof Resvg;
    }
  | undefined;

export async function loadResvg(
  prefix: string[],
  join?: typeof pathJoiner,
  loadBuffer?: (prefix: string[], filename: string) => Promise<ArrayBuffer>
): Promise<void> {
  if (resvgWasm !== undefined) {
    return;
  }

  if (!join) {
    join = pathJoiner;
  }

  // Dynamically import the WASM module
  const module = await import('@resvg/resvg-wasm');

  // Initialize WASM - different for browser vs Node.js tests
  if (loadBuffer) {
    // In Node.js tests - use loadBuffer to get the WASM file
    const wasmBuffer = await loadBuffer(prefix, 'index_bg.wasm');
    await module.initWasm(wasmBuffer);
  } else {
    // In browser - fetch and verify we're not getting HTML fallbacks
    const wasmPath = join(prefix.concat('index_bg.wasm'));
    const resp = await fetch(wasmPath, { cache: 'no-cache' });
    if (!resp.ok) {
      throw new Error(
        `resvg wasm not found at ${wasmPath}: ${resp.status} ${resp.statusText}`
      );
    }
    const ct = resp.headers.get('content-type') ?? '';
    if (!ct.includes('application/wasm')) {
      // Helpful head of body for diagnosis (won't be huge due to slice)
      const head = await resp
        .clone()
        .text()
        .then(t => t.slice(0, 80))
        .catch(() => '');
      throw new Error(
        `resvg wasm wrong content-type (${ct}) at ${wasmPath}. Body starts: ${JSON.stringify(head)}`
      );
    }
    await module.initWasm(resp);
  }

  resvgWasm = {
    initWasm: module.initWasm,
    Resvg: module.Resvg
  };
}

export function getResvg(): ResvgConstructor {
  if (!resvgWasm) {
    throw new Error('Resvg WASM not initialized. Call loadResvg() first.');
  }
  return resvgWasm.Resvg;
}

/**
 * Safely get the Resvg constructor with proper error handling and type safety
 */
export function getSafeResvg(): ResvgConstructor {
  try {
    return getResvg();
  } catch (error) {
    throw new Error(
      `Failed to get Resvg constructor: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
